/**
 * `parties/:partyId/calls` — one customer's call history, as a page (overnight v20 §D2).
 *
 * This route existed because there was no party record page in this app: the panel
 * needed a thin routed home, linked from the Parties report. ⚠ THAT PAGE HAS NOW LANDED
 * (`PartyRecordPage`, `parties/:partyId` — the feat/party-referral-screen cluster) and
 * it embeds `PartyCallHistoryPanel` exactly as this header predicted, at the one-line
 * cost it predicted; the report's customer column now points there, and its "Full log"
 * action points here. This page STAYS MOUNTED for deep links and old URLs — the header
 * said it would "become a redirect or go away", and choosing which is an IA decision
 * for the owner, deliberately not folded into that cluster.
 *
 * The page adds NOTHING to the panel but a header and a way back to the whole register.
 * A page that re-implemented the fetch would be a second copy of the read.
 */

import { Link, useParams } from 'react-router-dom';

import { useCountryCode } from '../../auth/guards';
import { PageHeader } from '../../components/shell/AppShell';
import { Alert } from '../../components/ui';
import { PartyCallHistoryPanel } from './PartyCallHistoryPanel';

export function PartyCallHistoryPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { partyId } = useParams<{ partyId: string }>();

  if (partyId === undefined || partyId === '') {
    // Reachable only by a hand-typed URL; say what is missing rather than rendering an
    // empty panel that reads as "this customer has never been called".
    return (
      <div className="flex flex-col gap-4 p-6">
        <Alert tone="danger" title="No customer was named in this address">
          A call history needs a customer. Open one from the customer register.
        </Alert>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-6">
      <PageHeader title="Call history" subtitle={`Customer ${partyId} · ${countryCode}`} />
      <div className="flex flex-wrap items-center gap-3">
        <Link
          to={`/countries/${countryCode}/party-calls`}
          className="text-body13 font-medium text-indigo hover:underline"
        >
          Every customer&apos;s calls
        </Link>
        <Link
          to={`/countries/${countryCode}/party-calls?party_id=${partyId}`}
          className="text-body13 font-medium text-indigo hover:underline"
        >
          This customer, with filters and paging
        </Link>
      </div>
      <div className="rounded-md border border-line bg-surface px-4">
        <PartyCallHistoryPanel countryCode={countryCode} partyId={partyId} />
      </div>
    </div>
  );
}
